var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { getABIDecodedValue } from "@algorandfoundation/algokit-utils/types/app-arc56";
import { encodeAddress, getApplicationAddress, makeEmptyTransactionSigner } from "algosdk";
import pMap from "p-map";
import { LOG_CLAIMS_PER_CALL, LOG_CLAIMS_PER_GROUP } from "./constants";
import { BirthdayNftDispenserClient } from "./generated/BirthdayNftDispenserClient";
import { chunk } from "./util/chunk";
import { chunked } from "./util/chunked";
import { SIMULATE_PARAMS } from "./util/increaseBudget";
import { wrapErrors } from "./util/wrapErrors";
/** The ABI encoding of WhitelistEntry[] (struct names are erased in array returns) */
const WHITELIST_ENTRIES_ABI_TYPE = "(uint8,uint64)[]";
const toEntries = (tuples) => tuples.map(([assetId, quantity]) => ({ assetId: Number(assetId), quantity }));
/**
 * Read-only SDK for the BirthdayNftDispenser contract.
 * All methods work without a writer account / signer.
 */
export class DispenserReaderSDK {
    algorand;
    appId;
    appAddress;
    /** Read client. Default sender is only used for readonly simulate calls. */
    readClient;
    concurrency;
    debug;
    constructor({ algorand, appId, readerAccount, concurrency = 2, debug = false }) {
        this.algorand = algorand;
        this.appId = BigInt(appId);
        this.appAddress = getApplicationAddress(this.appId);
        this.concurrency = concurrency;
        this.debug = debug;
        this.readClient = new BirthdayNftDispenserClient({
            algorand,
            appId: this.appId,
            // the app address always exists and is funded (MBR), so simulate-based reads
            // work without a configured reader account
            defaultSender: readerAccount ?? this.appAddress.toString(),
            defaultSigner: makeEmptyTransactionSigner(),
        });
    }
    /** Swallow algod's 404 for never-created boxes, returning undefined instead */
    async boxValueOrUndefined(read) {
        try {
            return await read();
        }
        catch (e) {
            if (`${e?.message ?? e}`.includes("box not found"))
                return undefined;
            throw e;
        }
    }
    /** Current admin address */
    async getAdmin() {
        const admin = await this.readClient.state.global.admin();
        if (admin === undefined) {
            throw new Error("admin global state not found - is the appId correct?");
        }
        return admin;
    }
    /**
     * The Algorand address whose ed25519 key signs claim tokens
     * (undefined = token claims disabled). The on-chain value is the raw 32-byte
     * public key; it is encoded here as a standard Algorand address.
     */
    async getMidnightTokenSigner() {
        const state = await this.readClient.state.global.midnightTokenSigner();
        const raw = state?.asByteArray();
        return raw && raw.length === 32 ? encodeAddress(raw) : undefined;
    }
    // Asset configs
    /** The config for an asset (real ASA id), or undefined if not registered */
    async getAssetConfig(assetId) {
        return this.boxValueOrUndefined(() => this.readClient.state.box.assetConfigs.value(assetId));
    }
    /** All registered asset configs, keyed by real ASA id */
    async getAssetConfigs() {
        return this.readClient.state.box.assetConfigs.getMap();
    }
    // Supply
    /**
     * Units of `assetId` the dispenser still holds and can hand out. The contract
     * has no separate "total supply" — it enforces the cap simply by refusing to
     * claim once the app account's balance hits 0 (claim asserts balance > 0), so
     * remaining supply is just what was funded into the app. Returns 0n if the app
     * is not opted into / holds none of the asset.
     */
    async getRemainingSupply(assetId) {
        try {
            const info = await this.algorand.client.algod.accountAssetInformation(this.appAddress, BigInt(assetId)).do();
            return BigInt(info.assetHolding?.amount ?? 0n);
        }
        catch (e) {
            const status = e?.status;
            if (status === 404 || `${e?.message ?? e}`.toLowerCase().includes("not found"))
                return 0n;
            throw e;
        }
    }
    /**
     * Supply snapshot for an asset: `claimed` is the on-chain running claim counter
     * (AssetConfig.claimCount, incremented on every successful claim), `remaining`
     * is the dispenser's current held balance (see getRemainingSupply). `total` is
     * their sum — the original funded amount, assuming all claimed units left via
     * claim(). Returns undefined if the asset is not registered.
     */
    async getAssetSupply(assetId) {
        const config = await this.getAssetConfig(assetId);
        if (!config)
            return undefined;
        const remaining = await this.getRemainingSupply(assetId);
        const claimed = config.claimCount;
        return { claimed, remaining, total: claimed + remaining };
    }
    // Whitelist reads
    /** All whitelist entries for an account (empty if none) */
    async getWhitelistEntries(account) {
        const tuples = await this.boxValueOrUndefined(() => this.readClient.state.box.whitelist.value(account.toString()));
        return toEntries(tuples ?? []);
    }
    /** Claims remaining of `assetId` for an account (0n if not whitelisted for it) */
    async getClaimsRemaining(account, assetId) {
        const config = await this.getAssetConfig(assetId);
        if (!config)
            return 0n;
        const entries = await this.getWhitelistEntries(account);
        return entries.find((entry) => entry.assetId === Number(config.id))?.quantity ?? 0n;
    }
    /** Whether an account has at least one claim of `assetId` remaining */
    async isWhitelisted(account, assetId) {
        return (await this.getClaimsRemaining(account, assetId)) > 0n;
    }
    /** Whether an account has already claimed `assetId` with a token */
    async hasClaimedWithToken(account, assetId) {
        const config = await this.getAssetConfig(assetId);
        if (!config)
            return false;
        const claimedIds = await this.boxValueOrUndefined(() => this.readClient.state.box.claimedTokens.value(account.toString()));
        return (claimedIds ?? []).some((id) => Number(id) === Number(config.id));
    }
    /** Full whitelist as a map of address -> entries */
    async getWhitelist() {
        const raw = await this.readClient.state.box.whitelist.getMap();
        return new Map([...raw.entries()].map(([account, tuples]) => [account, toEntries(tuples)]));
    }
    /**
     * Batch read whitelist allocations for many accounts (in input order; empty
     * array if not whitelisted). One simulated group per LOG_CLAIMS_PER_GROUP
     * accounts, spread over logClaimsRemaining app calls of up to
     * LOG_CLAIMS_PER_CALL accounts each; entries are decoded from the logs.
     */
    async getClaimsRemainingMulti(accounts) {
        const builder = this.readClient.newGroup();
        for (const callAccounts of chunk(accounts, LOG_CLAIMS_PER_CALL)) {
            builder.logClaimsRemaining({ args: { accounts: callAccounts } });
        }
        const { confirmations } = await builder.simulate(SIMULATE_PARAMS);
        const logs = confirmations.flatMap(({ logs }) => logs ?? []);
        return logs.map((log) => toEntries(getABIDecodedValue(new Uint8Array(log), WHITELIST_ENTRIES_ABI_TYPE, this.readClient.appSpec.structs)));
    }
    /**
     * All whitelisted accounts with their allocations (whitelist box-name scan
     * + batched logClaimsRemaining reads). Includes spent (zero-quantity) entries.
     */
    async getAccounts() {
        const boxNames = await this.algorand.app.getBoxNames(this.appId);
        const accounts = boxNames
            .filter(({ nameRaw }) => nameRaw[0] === 0x77 && nameRaw.length === 33) // 'w' prefix + 32-byte address
            .map(({ nameRaw }) => encodeAddress(nameRaw.slice(1)).toString());
        if (accounts.length === 0)
            return new Map();
        const entries = await this.getClaimsRemainingMulti(accounts);
        return new Map(accounts.map((account, i) => [account, entries[i]]));
    }
    /**
     * Every existing token-claim box as a map of address -> the asset-config numeric ids
     * that account has token-claimed (the raw box contents). Scans the claimedTokens
     * boxes ('t' prefix) and reads each one with `concurrency` parallelism.
     *
     * Pass `assetIds` (real ASA ids) to keep only accounts that token-claimed at least one
     * of them - useful for picking which accounts to tear down after a specific drop. ASA
     * ids that aren't registered are ignored; an empty/omitted list returns every account.
     */
    async getClaimedTokens(assetIds) {
        const boxNames = await this.algorand.app.getBoxNames(this.appId);
        const accounts = boxNames
            .filter(({ nameRaw }) => nameRaw[0] === 0x74 && nameRaw.length === 33) // 't' prefix + 32-byte address
            .map(({ nameRaw }) => encodeAddress(nameRaw.slice(1)).toString());
        if (accounts.length === 0)
            return new Map();
        const claimedLists = await pMap(accounts, async (account) => {
            const ids = await this.boxValueOrUndefined(() => this.readClient.state.box.claimedTokens.value(account));
            return (ids ?? []).map(Number);
        }, { concurrency: this.concurrency });
        let result = new Map(accounts.map((account, i) => [account, claimedLists[i]]));
        if (assetIds && assetIds.length > 0) {
            // Resolve the real ASA ids to their numeric config ids, then keep only accounts
            // whose claimed-id list intersects the target set.
            const configs = await this.getAssetConfigs();
            const targetNumericIds = new Set(assetIds.map((id) => configs.get(BigInt(id))?.id).filter((id) => id !== undefined));
            if (targetNumericIds.size === 0)
                return new Map();
            result = new Map([...result].filter(([, ids]) => ids.some((id) => targetNumericIds.has(id))));
        }
        return result;
    }
}
__decorate([
    chunked(LOG_CLAIMS_PER_GROUP),
    wrapErrors()
], DispenserReaderSDK.prototype, "getClaimsRemainingMulti", null);
